import { useState, useEffect, useCallback } from 'react';
import useMacroActions from './useMacroActions';
import useLanguages from './useLanguages';
import { useToast } from '../../../Context/ToastContext';
import { useConfirm } from '../../../Context/ConfirmContext';

/**
 * Custom hook for managing macros
 * @returns {Object} Macros state and operations
 */
const useMacros = () => {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  // Predefined macros
  const defaultMacros = [
    { id: 1, name: 'Greeting', content: 'Hello, my name is [name]. Nice to meet you.', category: 'General' },
    { id: 2, name: 'Follow-up', content: 'I\'m writing to follow up on our previous conversation about [topic].', category: 'Business' },
    { id: 3, name: 'Thank You', content: 'Thank you for your help with [task/project].', category: 'General' },
    { id: 4, name: 'Meeting Request', content: 'Are you available for a meeting on [date/time] to discuss [topic]?', category: 'Business' },
  ];

  // Macros state
  const [macros, setMacros] = useState([]);  // Initialize with empty array
  const [customMacros, setCustomMacros] = useState([]); // Add separate state for customMacros
  const [modalOpen, setModalOpen] = useState(false);
  const [currentMacro, setCurrentMacro] = useState(null);
  
  // Directive states that were previously defined
  const [incomeDirective, setIncomeDirective] = useState(
    'Find all the incomes that the user mentioned during this conversation.'
  );
  const [balanceDirective, setBalanceDirective] = useState(
    'Find all income and expense transactions mentioned in the conversation, list them chronologically, and calculate the total balance (income minus expenses).'
  );
  
  // Modal states
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // New macro form
  const [newMacro, setNewMacro] = useState({
    name: '',
    icon: '',
    directive: ''
  });
  
  // Load macros from localStorage on mount
  useEffect(() => {
    const savedMacros = localStorage.getItem('customMacros');
    if (savedMacros) {
      try {
        const parsed = JSON.parse(savedMacros);
        setMacros(parsed);
        setCustomMacros(parsed); // Update both macros and customMacros
      } catch (error) {
        console.error('Error parsing saved macros:', error);
        localStorage.removeItem('customMacros');
      }
    }
  }, []);
  
  // Save macros to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('customMacros', JSON.stringify(macros));
    setCustomMacros(macros); // Keep customMacros in sync with macros
  }, [macros]);
  
  // Handle macro click
  const handleMacroClick = useCallback((macro) => {
    // In a real implementation, this would insert the macro text 
    // into the current active input field or document
    console.log('Macro clicked:', macro.content);
    // Example: navigator.clipboard.writeText(macro.content);
  }, []);
  
  // Handle macro context menu (right-click)
  const handleMacroContextMenu = useCallback((e, macro) => {
    e.preventDefault();
    setCurrentMacro(macro);
    setModalOpen(true);
  }, []);
  
  // Add new macro
  const handleAddMacro = useCallback(() => {
    setCurrentMacro(null);
    setModalOpen(true);
  }, []);
  
  // Save macro (create or update)
  const handleSaveMacro = useCallback((macroData) => {
    if (macroData.id) {
      // Update existing macro
      setMacros(prev => 
        prev.map(macro => macro.id === macroData.id ? macroData : macro)
      );
    } else {
      // Create new macro with generated ID
      const newMacro = {
        ...macroData,
        id: Math.max(0, ...macros.map(m => m.id)) + 1
      };
      setMacros(prev => [...prev, newMacro]);
    }
    setModalOpen(false);
  }, [macros]);
  
  // Delete macro
  const handleDeleteMacro = useCallback((id) => {
    setMacros(prev => prev.filter(macro => macro.id !== id));
    setModalOpen(false);
  }, []);
  
  // Get other hooks
  const { 
    createDirectiveWithConversation, 
    sendToChatbot
  } = useMacroActions();
  
  const {
    selectedLanguage,
    getLanguageName,
    handleRightClick: handleLanguageRightClick,
    setShowLanguageDropdown
  } = useLanguages();
  
  // List of available icons for custom macros
  const availableIcons = [
    '💬', '📊', '📝', '📈', '📉', '🔍', '📌', '📋', '💰', 
    '💲', '💹', '🏦', '💵', '💳', '🧮', '📅', '⚙️', '🔄', 
    '🔔', '🎯', '📱', '💻', '📩', '📤', '📥', '💡', '🔑', 
    '🔎', '🗂️', '📂', '��', '✅'
  ];
  
  // Handle translate macro click - now uses conversation instead of summary
  const handleTranslateClick = (e) => {
    e.preventDefault();
    // Use getLanguageName which is already adapted to work with the new language structure
    const langName = getLanguageName(selectedLanguage);
    const directive = `Translate the following conversation to ${langName}:`;
    const fullDirective = createDirectiveWithConversation(directive);
    if (fullDirective) {
      sendToChatbot(fullDirective);
    }
  };
  
  // Handle translate edit button click - show language dropdown
  const handleTranslateEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowLanguageDropdown(true);
  };
  
  // Handle income macro click
  const handleIncomeClick = (e) => {
    e.preventDefault();
    const fullDirective = createDirectiveWithConversation(incomeDirective);
    if (fullDirective) {
      sendToChatbot(fullDirective);
    }
  };
  
  // Handle balance macro click
  const handleBalanceClick = (e) => {
    e.preventDefault();
    const fullDirective = createDirectiveWithConversation(balanceDirective);
    if (fullDirective) {
      sendToChatbot(fullDirective);
    }
  };
  
  // Handle custom macro click
  const handleCustomMacroClick = (macro) => {
    const fullDirective = createDirectiveWithConversation(macro.directive);
    if (fullDirective) {
      sendToChatbot(fullDirective);
    }
  };
  
  // Handle income macro right click (edit directive)
  const handleIncomeRightClick = (e) => {
    e.preventDefault();
    setShowIncomeModal(true);
  };
  
  // Handle balance macro right click (edit directive)
  const handleBalanceRightClick = (e) => {
    e.preventDefault();
    setShowBalanceModal(true);
  };
  
  // Handle "Add Custom Macro" button click
  const handleAddCustomMacro = () => {
    setNewMacro({
      name: '',
      icon: availableIcons[0],
      directive: 'Analyze the following conversation and '
    });
    setShowCreateModal(true);
  };
  
  // Handle create macro from form
  const handleCreateMacro = () => {
    if (!newMacro.name || !newMacro.icon || !newMacro.directive) {
      showToast('Please fill in all fields', 'warning');
      return;
    }

    const newCustomMacros = [
      ...macros,
      {
        id: Date.now(), // Use timestamp as unique ID
        name: newMacro.name,
        icon: newMacro.icon,
        directive: newMacro.directive
      }
    ];
    
    setMacros(newCustomMacros);
    setShowCreateModal(false);
  };
  
  // Handle delete custom macro
  const handleDeleteCustomMacro = async (id, e) => {
    e.stopPropagation(); // Prevent triggering the macro click
    
    const confirmed = await confirm({
      title: 'Delete Macro',
      message: 'Are you sure you want to delete this macro?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });
    
    if (confirmed) {
      setMacros(macros.filter(macro => macro.id !== id));
      showToast('Macro deleted', 'success');
    }
  };
  
  // Modal action handlers
  const handleIncomeModalSave = () => {
    setShowIncomeModal(false);
  };
  
  const handleIncomeModalCancel = () => {
    setShowIncomeModal(false);
  };
  
  const handleBalanceModalSave = () => {
    setShowBalanceModal(false);
  };
  
  const handleBalanceModalCancel = () => {
    setShowBalanceModal(false);
  };
  
  const handleCreateModalCancel = () => {
    setShowCreateModal(false);
  };
  
  return {
    // Predefined macros
    incomeDirective,
    setIncomeDirective,
    balanceDirective,
    setBalanceDirective,
    
    // Modal states
    showIncomeModal,
    showBalanceModal,
    showCreateModal,
    
    // Custom macros
    macros,
    customMacros, // Explicitly return customMacros
    newMacro,
    setNewMacro,
    availableIcons,
    
    // Event handlers
    handleTranslateClick,
    handleTranslateEdit,
    handleIncomeClick,
    handleBalanceClick,
    handleCustomMacroClick,
    handleLanguageRightClick,
    handleIncomeRightClick,
    handleBalanceRightClick,
    handleAddCustomMacro,
    handleCreateMacro,
    handleDeleteCustomMacro,
    
    // Modal action handlers
    handleIncomeModalSave,
    handleIncomeModalCancel,
    handleBalanceModalSave,
    handleBalanceModalCancel,
    handleCreateModalCancel,
    
    // New macro form
    modalOpen,
    setModalOpen,
    currentMacro,
    handleMacroClick,
    handleMacroContextMenu,
    handleAddMacro,
    handleSaveMacro,
    handleDeleteMacro
  };
};

export default useMacros; 